// DailyExpenses.js
import React, { useState, useEffect, useRef } from "react";
import api from "../Service/api";
import "./DailyExpenses.css";

function DailyExpenses() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerms, setSearchTerms] = useState({});
  const [showDropdowns, setShowDropdowns] = useState({});
  const [receivableAmount, setReceivableAmount] = useState({
    amount: "",
    description: "",
    date: new Date().toISOString().split("T")[0]
  });
  const [currentBalance, setCurrentBalance] = useState(0);
  const dropdownRefs = useRef({});

  useEffect(() => {
    initializeForm();
    fetchCurrentBalance();
  }, []);

  const fetchCurrentBalance = async () => {
    try {
      const response = await api.get("/Expenses/get/balance");
      setCurrentBalance(response.data.balance || 0);
    } catch (err) {
      console.error("Failed to fetch balance", err);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(showDropdowns).forEach((idx) => {
        if (showDropdowns[idx] && dropdownRefs.current[idx] && !dropdownRefs.current[idx].contains(event.target)) {
          setShowDropdowns(prev => ({ ...prev, [idx]: false }));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdowns]);

  const initializeForm = async () => {
    const initialRow = {
      type: "DAILY",
      productivityType: "PRODUCTIVE",
      category: "",
      description: "",
      amount: "",
      expenseDate: new Date().toISOString().split("T")[0],
      categoriesOptions: []
    };
    
    try {
      const res = await api.get("/Expenses/get/category", {
        params: { type: initialRow.type }
      });
      initialRow.categoriesOptions = res.data || [];
    } catch (err) {
      console.error("Failed to fetch categories", err);
    }
    
    setExpenses([initialRow]);
  };

  const fetchCategories = async (type, index) => {
    try {
      const res = await api.get("/Expenses/get/category", { params: { type } });
      const updated = [...expenses];
      updated[index].categoriesOptions = res.data || [];
      setExpenses(updated);
    } catch (err) {
      console.error("Failed to fetch categories", err);
    }
  };

  const handleChange = (index, field, value) => {
    const updated = [...expenses];
    updated[index][field] = value;

    if (field === "type") {
      updated[index].category = "";
      setSearchTerms(prev => ({ ...prev, [index]: "" }));
      fetchCategories(value, index);
    }

    setExpenses(updated);
  };

  const handleReceivableAmountChange = (field, value) => {
    setReceivableAmount(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearchChange = (index, value) => {
    setSearchTerms(prev => ({ ...prev, [index]: value }));
    setShowDropdowns(prev => ({ ...prev, [index]: true }));
  };

  const handleCategorySelect = (index, categoryName) => {
    const updated = [...expenses];
    updated[index].category = categoryName;
    setExpenses(updated);
    setShowDropdowns(prev => ({ ...prev, [index]: false }));
    setSearchTerms(prev => ({ ...prev, [index]: "" }));
  };

  const toggleDropdown = (index) => {
    setShowDropdowns(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const getFilteredCategories = (exp) => {
    const index = expenses.findIndex(e => e === exp);
    const searchTerm = searchTerms[index] || "";
    
    if (!exp.categoriesOptions || !Array.isArray(exp.categoriesOptions)) {
      return [];
    }
    
    if (!searchTerm.trim()) return exp.categoriesOptions;
    
    return exp.categoriesOptions.filter(cat => 
      cat.name && cat.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getInputValue = (index, exp) => {
    if (searchTerms[index] !== undefined && searchTerms[index] !== "") {
      return searchTerms[index];
    }
    return exp.category || "";
  };

  const addRow = async () => {
    const newRow = {
      type: "DAILY",
      productivityType: "PRODUCTIVE",
      category: "",
      description: "",
      amount: "",
      expenseDate: new Date().toISOString().split("T")[0],
      categoriesOptions: []
    };

    try {
      const res = await api.get("/Expenses/get/category", {
        params: { type: newRow.type }
      });
      newRow.categoriesOptions = res.data || [];
    } catch (err) {
      console.error("Failed to fetch categories for new row", err);
    }

    setExpenses(prev => [...prev, newRow]);
  };

  const removeRow = (index) => {
    const updated = expenses.filter((_, i) => i !== index);
    setExpenses(updated);
    setSearchTerms(prev => {
      const newTerms = { ...prev };
      delete newTerms[index];
      return newTerms;
    });
    setShowDropdowns(prev => {
      const newShows = { ...prev };
      delete newShows[index];
      return newShows;
    });
  };

  const saveReceivableAmount = async () => {
    if (!receivableAmount.amount || receivableAmount.amount <= 0) {
      alert("Please enter a valid receivable amount");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        amount: parseFloat(receivableAmount.amount),
        description: receivableAmount.description || "",
        date: receivableAmount.date || new Date().toISOString().split("T")[0],
        type: "RECEIVABLE"
      };

      await api.post("/Expenses/add/receivable", payload);
      alert("Receivable amount added successfully!");
      
      setReceivableAmount({
        amount: "",
        description: "",
        date: new Date().toISOString().split("T")[0]
      });
      
      await fetchCurrentBalance();
      
    } catch (err) {
      console.error("Error saving receivable amount", err);
      alert(err.response?.data?.message || "Failed to save receivable amount");
    } finally {
      setLoading(false);
    }
  };

  const saveExpenses = async () => {
    setLoading(true);
    try {
      let totalExpenseAmount = 0;
      for (let i = 0; i < expenses.length; i++) {
        const exp = expenses[i];
        
        if (!exp.category) {
          throw new Error(`Row ${i + 1}: Category is required.`);
        }
        
        if (!exp.amount || exp.amount <= 0) {
          throw new Error(`Row ${i + 1}: Amount must be greater than 0.`);
        }
        
        if (!exp.productivityType) {
          throw new Error(`Row ${i + 1}: Productivity type is required.`);
        }
        
        totalExpenseAmount += parseFloat(exp.amount);
      }

      if (totalExpenseAmount > currentBalance) {
        throw new Error(`Insufficient balance! Available: ${currentBalance.toFixed(2)}, Required: ${totalExpenseAmount.toFixed(2)}`);
      }

      const payload = expenses.map((e) => ({
        category: e.category,
        amount: parseFloat(e.amount),
        expenseType: e.type,
        productivityType: e.productivityType,
        description: e.description || "",
        expenseDate: e.expenseDate || new Date().toISOString().split("T")[0],
      }));

      await api.post("/Expenses/add", payload);
      alert(`Expenses saved! Total: ${totalExpenseAmount.toFixed(2)} deducted.`);
      
      await initializeForm();
      setSearchTerms({});
      setShowDropdowns({});
      await fetchCurrentBalance();
      
    } catch (err) {
      console.error("Error saving expenses", err);
      alert(err.message || "Failed to save expenses.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="daily-expenses-container">
      <h2>💰 Expense Management System</h2>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Saving data...</p>
        </div>
      )}

      {/* Current Balance Card */}
      <div className="balance-section">
        <div className="balance-card">
          <h3>Current Balance</h3>
          <div className={`balance-amount ${currentBalance >= 0 ? 'positive' : 'negative'}`}>
            ₹ {currentBalance.toFixed(2)}
          </div>
          <p className="balance-info">Available for expenses</p>
        </div>
      </div>

      {/* Receive Amount Section */}
      <div className="receivable-section">
        <h3>➕ Add Receivable Amount</h3>
        <div className="receivable-form">
          <div className="form-row">
            <div className="form-group">
              <label>Amount *</label>
              <input
                type="number"
                className="input-field"
                value={receivableAmount.amount}
                placeholder="Enter amount"
                onChange={(e) => handleReceivableAmountChange("amount", e.target.value)}
                disabled={loading}
                step="0.01"
              />
            </div>
            
            <div className="form-group">
              <label>Description</label>
              <input
                type="text"
                className="input-field"
                value={receivableAmount.description}
                placeholder="Optional"
                onChange={(e) => handleReceivableAmountChange("description", e.target.value)}
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <label>Date</label>
              <input
                type="date"
                className="input-field"
                value={receivableAmount.date}
                onChange={(e) => handleReceivableAmountChange("date", e.target.value)}
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <button 
                className="add-receivable-btn" 
                onClick={saveReceivableAmount}
                disabled={loading}
              >
                + Add to Balance
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Expenses Section */}
      <div className="expenses-section">
        <h3>➖ Add Expenses</h3>
        
        {/* Expense Cards - Better than table for mobile/responsive */}
        <div className="expenses-cards">
          {expenses.map((exp, idx) => (
            <div key={idx} className="expense-card">
              <div className="card-header">
                <span className="card-title">Expense #{idx + 1}</span>
                {expenses.length > 1 && (
                  <button className="remove-btn" onClick={() => removeRow(idx)} disabled={loading}>
                    ✕ Remove
                  </button>
                )}
              </div>
              
              <div className="card-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Type *</label>
                    <select
                      className="input-field"
                      value={exp.type}
                      onChange={(e) => handleChange(idx, "type", e.target.value)}
                      disabled={loading}
                    >
                      <option value="DAILY">📅 Daily</option>
                      <option value="MONTHLY">📆 Monthly</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Productivity Type *</label>
                    <select
                      className={`input-field productivity-select ${exp.productivityType === 'PRODUCTIVE' ? 'productive' : 'non-productive'}`}
                      value={exp.productivityType}
                      onChange={(e) => handleChange(idx, "productivityType", e.target.value)}
                      disabled={loading}
                    >
                      <option value="PRODUCTIVE">✅ PRODUCTIVE</option>
                      <option value="NON_PRODUCTIVE">❌ NON-PRODUCTIVE</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Category *</label>
                    <div className="searchable-dropdown" ref={el => dropdownRefs.current[idx] = el}>
                      <div className="dropdown-input-wrapper">
                        <input
                          type="text"
                          className="input-field"
                          value={getInputValue(idx, exp)}
                          onChange={(e) => handleSearchChange(idx, e.target.value)}
                          onFocus={() => setShowDropdowns(prev => ({ ...prev, [idx]: true }))}
                          placeholder="Search category..."
                          disabled={loading}
                          autoComplete="off"
                        />
                        <button
                          type="button"
                          className="dropdown-toggle-btn"
                          onClick={() => toggleDropdown(idx)}
                          disabled={loading}
                        >
                          ▼
                        </button>
                      </div>
                      
                      {showDropdowns[idx] && (
                        <div className="dropdown-list">
                          {getFilteredCategories(exp).length > 0 ? (
                            getFilteredCategories(exp).map((cat, catIdx) => (
                              <div
                                key={cat.id || cat.name || catIdx}
                                className="dropdown-item"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleCategorySelect(idx, cat.name);
                                }}
                              >
                                {cat.name}
                              </div>
                            ))
                          ) : (
                            <div className="dropdown-item no-results">
                              No categories found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Amount *</label>
                    <input
                      className="input-field"
                      type="number"
                      value={exp.amount}
                      placeholder="Enter amount"
                      onChange={(e) => handleChange(idx, "amount", e.target.value)}
                      disabled={loading}
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label>Expense Date</label>
                    <input
                      className="input-field"
                      type="date"
                      value={exp.expenseDate}
                      onChange={(e) => handleChange(idx, "expenseDate", e.target.value)}
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <input
                      className="input-field"
                      type="text"
                      value={exp.description}
                      placeholder="Optional"
                      onChange={(e) => handleChange(idx, "description", e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="action-buttons">
          <button className="add-btn" onClick={addRow} disabled={loading}>
            + Add Another Expense
          </button>
          <button className="save-btn" onClick={saveExpenses} disabled={loading}>
            💾 Deduct Expenses from Balance
          </button>
        </div>
      </div>
    </div>
  );
}

export default DailyExpenses;